"use client"

import type React from "react"
import { useState } from "react"
import Breadcrumb from "../../../utils/breadcrumbs"
import {
    FiSend, FiMail, FiMessageSquare,
    FiUsers, FiPlus, FiEye, FiBell
} from "react-icons/fi"
import toast from "react-hot-toast"
import api from "../../../libs/axios"
import ModalSelect from "../../../ui/select-modal"
import MultiSelectModal from "../../../ui/multipule-select-modal"

interface INotificationTemplate {
    id: number
    name: string
    subject: string
    message: string
    type: string
    status: string
}

interface NotificationFormData {
    template_id: string
    recipient_type: string
    recipient_ids: number[] | string
    subject: string
    message: string
    send_immediately: boolean
    scheduled_at: string
}

interface SelectOption {
    value: string | number
    label: string
    description?: string
    data?: any
}

interface PaginationData {
    current_page: number
    per_page: number
    total: number
    total_pages: number
    has_next_page: boolean
    has_prev_page: boolean
    next_page: number | null
    prev_page: number | null
}

interface FetchTemplatesResponse {
    data: SelectOption[]
    pagination: PaginationData
}

const SendNotification: React.FC = () => {
    const [formData, setFormData] = useState<NotificationFormData>({
        template_id: "",
        recipient_type: "all",
        recipient_ids: [],
        subject: "",
        message: "",
        send_immediately: true,
        scheduled_at: "",
    })

    const [loading, setLoading] = useState(false)
    const [templateLoading, setTemplateLoading] = useState(false)
    const [errors, setErrors] = useState<Partial<NotificationFormData>>({})
    const [selectedTemplate, setSelectedTemplate] = useState<INotificationTemplate | null>(null)

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target

        if (type === "checkbox") {
            const checked = (e.target as HTMLInputElement).checked
            setFormData((prev) => ({
                ...prev,
                [name]: checked,
            }))
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }))
        }

        if (errors[name as keyof NotificationFormData]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }))
        }
    }

    const fetchTemplates = async (
        page: number,
        search?: string,
        limit: number = 10,
        templateId?: string | number
    ): Promise<FetchTemplatesResponse> => {
        try {
            setTemplateLoading(true)
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search ? { search } : {}),
                ...(templateId ? { template_id: templateId.toString() } : {}),
            })

            const response = await api.get(`/notifications/templates/name?${params}`)
            const templatesData = response.data?.data || []
            const paginationData = response.data?.pagination

            const data: SelectOption[] = templatesData.map((template: INotificationTemplate) => ({
                value: template.id.toString(),
                label: template.name,
                description: `${template?.type?.toUpperCase()}`,
                data: template,
            }))

            const pagination: PaginationData = {
                current_page: paginationData?.current_page || page,
                per_page: paginationData?.per_page || limit,
                total: paginationData?.total || 0,
                total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
                has_next_page: paginationData?.has_next_page || false,
                has_prev_page: paginationData?.has_prev_page || page > 1,
                next_page: paginationData?.next_page || null,
                prev_page: paginationData?.prev_page || null,
            }

            return { data, pagination }
        } catch (error: any) {
            console.error("Error fetching templates:", error)
            toast.error("Failed to fetch templates")
            return {
                data: [],
                pagination: {
                    current_page: page,
                    per_page: limit,
                    total: 0,
                    total_pages: 0,
                    has_next_page: false,
                    has_prev_page: page > 1,
                    next_page: null,
                    prev_page: page > 1 ? page - 1 : null,
                },
            }
        } finally {
            setTemplateLoading(false)
        }
    }

    const fetchUsers = async (
        page: number,
        search?: string,
        limit: number = 10
    ): Promise<FetchTemplatesResponse> => {
        try {
            let url = `/users?page=${page}&limit=${limit}`

            if (search) {
                const params = new URLSearchParams()
                params.append('name', search)
                url += `&${params.toString()}`
            }

            const response = await api.get(url)
            const usersData = response.data?.data || []
            const paginationData = response.data?.pagination

            const data: SelectOption[] = usersData.map((user: any) => {
                const firstName = user?.details?.first_name || '';
                const lastName = user?.details?.last_name || '';
                const fullName = (firstName + ' ' + lastName).trim();

                return {
                    value: user.id,
                    label: fullName || 'Unknown User',
                    description: user?.phone_number || user?.email || 'No contact info',
                    data: user,
                };
            });

            const pagination: PaginationData = {
                current_page: paginationData?.current_page || page,
                per_page: paginationData?.per_page || limit,
                total: paginationData?.total || 0,
                total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
                has_next_page: paginationData?.has_next_page || false,
                has_prev_page: paginationData?.has_prev_page || page > 1,
                next_page: paginationData?.next_page || null,
                prev_page: paginationData?.prev_page || null,
            }

            return { data, pagination }
        } catch (error: any) {
            console.error("Error fetching users:", error)
            toast.error("Failed to fetch users")
            return {
                data: [],
                pagination: {
                    current_page: page,
                    per_page: limit,
                    total: 0,
                    total_pages: 0,
                    has_next_page: false,
                    has_prev_page: page > 1,
                    next_page: null,
                    prev_page: page > 1 ? page - 1 : null,
                },
            }
        }
    }

    const handleTemplateChange = (option: SelectOption) => {
        const template = option.data as INotificationTemplate

        setFormData((prev) => ({
            ...prev,
            template_id: option.value.toString(),
            subject: template.subject || "",
            message: template.message || "",
        }))

        setSelectedTemplate(template)

        if (errors.template_id) {
            setErrors((prev) => ({
                ...prev,
                template_id: "",
            }))
        }
    }

    const handleRecipientIdsChange = (ids: number[]) => {
        setFormData((prev) => ({
            ...prev,
            recipient_ids: ids,
        }))

        if (errors.recipient_ids) {
            setErrors((prev) => ({
                ...prev,
                recipient_ids: [],
            }))
        }
    }

    const validateForm = (): boolean => {
        const newErrors: Partial<NotificationFormData> = {}

        if (!formData.template_id) {
            newErrors.template_id = "Please select a template"
        }

        if (formData.recipient_type === "specific" && formData.recipient_ids.length === 0) {
            newErrors.recipient_ids = "Please select at least one user"
        }

        if (
            selectedTemplate &&
            (selectedTemplate.type === "email" || selectedTemplate.type === "push") &&
            !formData.subject.trim()
        ) {
            newErrors.subject = "Subject is required"
        }

        if (!formData.message.trim()) {
            newErrors.message = "Message is required"
        } else if (formData.message.trim().length < 10) {
            newErrors.message = "Message must be at least 10 characters"
        }

        if (!formData.send_immediately && !formData.scheduled_at) {
            newErrors.scheduled_at = "Please select a scheduled time"
        } else if (!formData.send_immediately && formData.scheduled_at) {
            const scheduledTime = new Date(formData.scheduled_at)
            const now = new Date()

            const year = formData.scheduled_at.split("-")[0]
            if (year.length !== 4) {
                newErrors.scheduled_at = "Year must be exactly 4 digits (YYYY)"
            } else if (scheduledTime <= now) {
                newErrors.scheduled_at = "Scheduled time must be in the future"
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            toast.error("Please fix the validation errors before submitting")
            return
        }

        setLoading(true)

        try {
            const payload: any = {
                template_id: parseInt(formData.template_id),
                recipient_type: formData.recipient_type,
                subject: formData.subject || null,
                message: formData.message,
                send_immediately: formData.send_immediately,
            }

            if (formData.recipient_type === "specific" && formData.recipient_ids.length > 0) {
                payload.recipient_ids = formData.recipient_ids
            }

            if (!formData.send_immediately && formData.scheduled_at) {
                payload.scheduled_at = new Date(formData.scheduled_at).toISOString()
            }

            const response = await api.post("/notifications/send", payload)

            console.log("Notification sent:", response.data)
            toast.success(
                formData.send_immediately ? "Notification sent successfully!" : "Notification scheduled successfully!"
            )

            setFormData({
                template_id: "",
                recipient_type: "all",
                recipient_ids: [],
                subject: "",
                message: "",
                send_immediately: true,
                scheduled_at: "",
            })
            setSelectedTemplate(null)
            setErrors({})
        } catch (error: any) {
            console.error("Send notification error:", error)
            const errorMessage = error?.response?.data?.message || "Failed to send notification"
            toast.error(errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="w-full min-h-screen bg-white p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Send Notification
                </h2>
                <Breadcrumb />
            </div>

            <div className="mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Form Section - Left Side */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow border">
                            <div className="p-6 border-b border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                    <FiSend className="mr-2" />
                                    Create & Send Notification
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    Select a template and customize your notification message
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6">
                                {/* Template Selection */}
                                <div className="mb-5">
                                    <h4 className="text-md font-medium text-gray-900 border-b pb-2 mb-3">
                                        <FiPlus className="inline mr-1" />
                                        Template Selection
                                    </h4>

                                    <ModalSelect
                                        label="Select Template"
                                        placeholder="Choose a template..."
                                        value={formData.template_id}
                                        onChange={handleTemplateChange}
                                        fetchOptions={(page, search, limit) =>
                                            fetchTemplates(page, search, limit, formData?.template_id)
                                        }
                                        required
                                        searchPlaceholder="Search templates..."
                                        error={errors.template_id}
                                        disabled={templateLoading}
                                        renderOption={(option) => (
                                            <div>
                                                <div className="font-medium text-gray-900">{option.label}</div>
                                                <div className="text-sm text-gray-500 capitalize flex items-center mt-0.5">
                                                    {option.description}
                                                </div>
                                            </div>
                                        )}
                                    />
                                </div>

                                {/* Recipients */}
                                <div className="mb-5">
                                    <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                                        <FiUsers className="inline mr-1" />
                                        Recipients
                                    </h4>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 mt-3">Send To</label>
                                        <select
                                            name="recipient_type"
                                            value={formData.recipient_type}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="all">All Users</option>
                                            <option value="active">Active Users Only</option>
                                            <option value="specific">Specific Users (Select from list)</option>
                                        </select>
                                    </div>

                                    {formData.recipient_type === "specific" && (
                                        <MultiSelectModal
                                            label="Select Users"
                                            placeholder="Click to select users..."
                                            selectedIds={formData.recipient_ids as number[]}
                                            onChange={handleRecipientIdsChange}
                                            fetchOptions={fetchUsers}
                                            required
                                            error={errors.recipient_ids as string}
                                            searchPlaceholder="Search users by name..."
                                            renderOption={(option) => (
                                                <div>
                                                    <div className="font-medium text-gray-900">{option.label}</div>
                                                    <div className="text-sm text-gray-500">{option.description}</div>
                                                </div>
                                            )}
                                        />
                                    )}
                                </div>

                                {/* Message Content */}
                                <div className="space-y-3 mb-5">
                                    <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                                        <FiMessageSquare className="inline mr-1" />
                                        Message Content
                                    </h4>

                                    {selectedTemplate &&
                                        (selectedTemplate.type === "email" || selectedTemplate.type === "push") && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <FiMail className="inline mr-1" />
                                                    Subject *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.subject ? "border-red-500" : "border-gray-300"
                                                        }`}
                                                    placeholder="Enter subject line"
                                                />
                                                {errors.subject && (
                                                    <p className="text-red-500 text-xs mt-1">{errors.subject}</p>
                                                )}
                                            </div>
                                        )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows={6}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.message ? "border-red-500" : "border-gray-300"
                                                }`}
                                            placeholder="Your message content will appear here when you select a template"
                                        />
                                        {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                                        <p className="text-xs text-gray-500 mt-1">
                                            You can edit the template message. Variables like {`{{name}}`}, {`{{email}}`}{" "}
                                            will be replaced with actual values.
                                        </p>
                                    </div>
                                </div>

                                {/* Scheduling */}
                                <div className="space-y-3 mb-5">
                                    <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                                        Scheduling Options
                                    </h4>

                                    <div className="flex items-center space-x-4">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="send_immediately"
                                                checked={formData.send_immediately}
                                                // onChange={handleInputChange}
                                                onChange={() => toast("Scheduling is not available yet.")}
                                                className="mr-2"
                                            />
                                            Send Immediately
                                        </label>
                                    </div>

                                    {!formData.send_immediately && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Schedule For *
                                            </label>
                                            <input
                                                type="datetime-local"
                                                name="scheduled_at"
                                                value={formData.scheduled_at}
                                                onChange={handleInputChange}
                                                min={new Date().toISOString().slice(0, 16)}
                                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.scheduled_at ? "border-red-500" : "border-gray-300"
                                                    }`}
                                            />
                                            {errors.scheduled_at && (
                                                <p className="text-red-500 text-xs mt-1">{errors.scheduled_at}</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFormData({
                                                template_id: "",
                                                recipient_type: "all",
                                                recipient_ids: [],
                                                subject: "",
                                                message: "",
                                                send_immediately: true,
                                                scheduled_at: "",
                                            })
                                            setSelectedTemplate(null)
                                            setErrors({})
                                        }}
                                        className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                {formData.send_immediately ? "Sending..." : "Scheduling..."}
                                            </>
                                        ) : (
                                            <>
                                                <FiSend className="mr-2" />
                                                {formData.send_immediately ? "Send Now" : "Schedule Notification"}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Preview Section - Right Side */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow border sticky top-6">
                            <div className="p-4 border-b border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                    <FiEye className="mr-2" />
                                    Live Preview
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">See how your notification will look</p>
                            </div>

                            <div className="p-4 space-y-4">
                                {selectedTemplate ? (
                                    <>
                                        {/* Template Info */}
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-gray-700">Template</span>
                                                <span
                                                    className={`px-2 py-1 text-xs rounded-full ${selectedTemplate.type === "email"
                                                        ? "bg-blue-100 text-blue-800"
                                                        : selectedTemplate.type === "sms"
                                                            ? "bg-green-100 text-green-800"
                                                            : "bg-purple-100 text-purple-800"
                                                        }`}
                                                >
                                                    {selectedTemplate?.type?.toUpperCase()}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600">{selectedTemplate.name}</p>
                                        </div>

                                        {/* Recipient Info */}
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="flex items-center mb-2">
                                                <FiUsers className="mr-2 text-gray-500" />
                                                <span className="text-sm font-medium text-gray-700">Recipients</span>
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                {formData.recipient_type === "all"
                                                    ? "All Users"
                                                    : formData.recipient_type === "active"
                                                        ? "Active Users Only"
                                                        : `${formData.recipient_ids.length} user(s) selected`}
                                            </p>
                                        </div>

                                        {/* Preview based on type */}
                                        {selectedTemplate.type === "email" && (
                                            <div className="border rounded-lg overflow-hidden">
                                                <div className="bg-gray-100 px-3 py-2 border-b">
                                                    <div className="flex items-center">
                                                        <FiMail className="mr-2 text-gray-500" />
                                                        <span className="text-sm font-medium">Email Preview</span>
                                                    </div>
                                                </div>
                                                <div className="p-4 bg-white">
                                                    {formData.subject && (
                                                        <div className="mb-3">
                                                            <span className="text-xs text-gray-500 uppercase tracking-wide">
                                                                Subject
                                                            </span>
                                                            <p className="font-medium text-gray-900 mt-1">
                                                                {formData.subject}
                                                            </p>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span className="text-xs text-gray-500 uppercase tracking-wide">
                                                            Message
                                                        </span>
                                                        <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">
                                                            {formData.message || "Your message will appear here..."}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {selectedTemplate.type === "sms" && (
                                            <div className="border rounded-lg overflow-hidden">
                                                <div className="bg-gray-100 px-3 py-2 border-b">
                                                    <div className="flex items-center">
                                                        <FiMessageSquare className="mr-2 text-gray-500" />
                                                        <span className="text-sm font-medium">SMS Preview</span>
                                                    </div>
                                                </div>
                                                <div className="p-4 bg-white">
                                                    <div className="bg-blue-500 text-white rounded-lg p-3 max-w-xs ml-auto">
                                                        <p className="text-sm">
                                                            {formData.message || "Your SMS message will appear here..."}
                                                        </p>
                                                        <p className="text-xs opacity-75 mt-1">
                                                            {new Date().toLocaleTimeString([], {
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {selectedTemplate.type === "push" && (
                                            <div className="border rounded-lg overflow-hidden">
                                                <div className="bg-gray-100 px-3 py-2 border-b">
                                                    <div className="flex items-center">
                                                        <FiBell className="mr-2 text-gray-500" />
                                                        <span className="text-sm font-medium">
                                                            Push Notification Preview
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="p-4 bg-white">
                                                    <div className="bg-gray-900 text-white rounded-lg p-3 max-w-sm">
                                                        <div className="flex items-start">
                                                            <div className="bg-blue-500 rounded p-1 mr-2 mt-0.5">
                                                                <FiBell className="w-3 h-3" />
                                                            </div>
                                                            <div className="flex-1">
                                                                {formData.subject && (
                                                                    <p className="font-medium text-sm mb-1">
                                                                        {formData.subject}
                                                                    </p>
                                                                )}
                                                                <p className="text-xs text-gray-300">
                                                                    {formData.message ||
                                                                        "Your push notification message will appear here..."}
                                                                </p>
                                                                <p className="text-xs text-gray-400 mt-1">now</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Scheduling Info */}
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="flex items-center mb-2">
                                                <span className="text-sm font-medium text-gray-700">Delivery</span>
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                {formData.send_immediately
                                                    ? "Send immediately"
                                                    : formData.scheduled_at
                                                        ? `Scheduled for ${new Date(formData.scheduled_at).toLocaleString()}`
                                                        : "Schedule not set"}
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-8">
                                        <FiPlus className="mx-auto h-12 w-12 text-gray-400" />
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No template selected</h3>
                                        <p className="mt-1 text-sm text-gray-500">Select a template to see the preview</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SendNotification